import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ShellService } from '../../shell/shell.service';

/**
 * Inspection page groups, as twin-ci gates them (status bars and controllers):
 *   intake   Client, Property, Scheduling (and creating an inspection)
 *   overview Overview
 *   profile  Inspection Info, Findings, Diagram, Confirm Inspection
 *   work     Job Info, Completion Tasks
 *   review   Completion Tasks' Review section (the old Confirm page)
 *   logs     Log History
 */
export type IntakeGroup = 'intake' | 'overview' | 'profile' | 'work' | 'review' | 'logs';

export type IntakeAccess = { [group in IntakeGroup]: boolean };

// Where a job opens for a user, in this order (used for redirects and the report links).
const JOB_PAGES: { group: IntakeGroup, route: string }[] = [
    { group: 'overview', route: 'inspections-completed-sheet' },
    { group: 'profile', route: 'inspections-profile' },
    { group: 'work', route: 'inspections-job' },
    { group: 'intake', route: 'inspections-order-parties' }
];

/**
 * Who may open which inspection page (UI only, as agreed 2026-10-13; the APIs are unchanged).
 * Administrators: everything. Inspectors: Overview and the inspection (profile) pages, as twin-ci's
 * inspector status bar. Staff levels: twin-ci's labels - 'Status Bar - Intake' (also Overview),
 * 'Status Bar - Profile' + 'Inspections' (the controllers' own check), 'Status Bar - Work', and
 * 'Work' for the Review section (twin-ci job_review). Log History is administrators only.
 */
@Injectable({ providedIn: 'root' })
export class IntakeAccessService {
    constructor(private shell: ShellService) { }

    access(): Observable<IntakeAccess> {
        const level = this.shell.user ? this.shell.user.user_level : '';
        if (level === 'administrator') {
            return of({ intake: true, overview: true, profile: true, work: true, review: true, logs: true });
        }
        if (level === 'inspector') {
            return of({ intake: false, overview: true, profile: true, work: false, review: false, logs: false });
        }
        return this.shell.permissions$.pipe(take(1), map(labels => {
            const has = (label: string) => (labels || []).indexOf(label) !== -1;
            const intake = has('Status Bar - Intake');
            return {
                intake,
                overview: intake,
                profile: has('Status Bar - Profile') && has('Inspections'),
                work: has('Status Bar - Work'),
                review: has('Work'),
                logs: false
            };
        }));
    }

    /** The first page of a job this user may open, or null. */
    jobHome(access: IntakeAccess, jobId: any): string | null {
        const page = JOB_PAGES.find(p => access[p.group]);
        return page && jobId ? '/' + page.route + '/' + jobId : null;
    }
}

/** Route data.access: the IntakeGroup the page belongs to. Elsewhere: the job's first allowed page, else the dashboard. */
@Injectable({ providedIn: 'root' })
export class IntakeAccessGuard implements CanActivate {
    constructor(private intakeAccess: IntakeAccessService, private router: Router) { }

    // Angular 6 guards cannot return a UrlTree, so this navigates and refuses.
    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const group: IntakeGroup = route.data && route.data.access;
        return this.intakeAccess.access().pipe(map(access => {
            if (!group || access[group]) {
                return true;
            }
            const home = this.intakeAccess.jobHome(access, route.params.jobId);
            this.router.navigateByUrl(home || '/dashboard');
            return false;
        }));
    }
}
